import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { createColumns } from "../../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../../constants/datatableStyles";
import PageTitle from "../../../Components/UI/PageTitle";
import EyeDetailsIcon from "../../../Components/Svgs/EyeDetailsIcon";
import RowLoader from "../../../Components/UI/RowLoader";
import Loader from "../../../Components/Loader";
import ErrorAnimationComponent from "../../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import LightButton from "../../../Components/UI/LightButton";
import { getListCourses } from "../../../apis";
import {
  convertBooleanValues,
  toSnakeCase,
  transformKeys,
} from "../../../_helper";
import CustomPagination from "../../../Components/CustomPagination";

const ListCoursesManagement = () => {
  const [coursesData, setCoursesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginator, setPaginator] = useState({});

  const location = useLocation();
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getListCourses(
          sortKey,
          sortDirection,
          currentPage
        );
        const handleBooleanValues = convertBooleanValues(response.data.data, [
          "isActive",
          "isSitePublished",
          "isDisplayed",
        ]);
        const handleDataToSnakeCase = handleBooleanValues.map((d) =>
          transformKeys(d)
        );
        setCoursesData(handleDataToSnakeCase);
        setPaginator({
          last_page: response.data.last_page,
          current_page: response.data.current_page,
        });
      } catch (err) {
        setError("Failed to load courses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [sortKey, sortDirection, currentPage]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (sortKey) params.set("sortKey", sortKey);
    params.set("page", currentPage);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [sortKey, sortDirection, navigate, currentPage]);

  const handleSortChange = (column, direction) => {
    let sortBy = toSnakeCase(column.sortKey);

    switch (sortBy) {
      case "emoney":
        sortBy = "e_money";
        break;
      case "is_site_published":
        sortBy = "is_published";
        break;
      default:
        break;
    }
    setSortKey(sortBy);
    setSortDirection(direction);
  };

  const hiddenColumns = ["id"];

  const customColumns = [
    {
      name: "Details",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <div className="tw-flex tw-gap-1">
          <Link to={`${row.id}`}>
            <EyeDetailsIcon width="30px" height="30px" />
          </Link>
        </div>
      ),
    },
  ];

  const columns = useMemo(() => {
    return createColumns(coursesData, customColumns, hiddenColumns, true).map(
      (col) => {
        if (col.name === "Course Image") {
          return {
            ...col,
            sortable: false,
            cell: (row) => (
              <div className="tw-w-[50px] tw-h-[50px] tw-rounded-full tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-bg-gray-200">
                <img
                  src={row.course_image}
                  alt="Course"
                  className="tw-w-full tw-h-full tw-object-cover"
                />
              </div>
            ),
          };
        }
        return {
          ...col,
          sortable: [
            "Created At",
            "E Money",
            "Course Name",
            "Is Active",
            "Is Displayed",
            "Is Site Published",
            "Created By",
          ].includes(col.name),
          width: col.name === "Num" ? "70px" : "1fr",
        };
      }
    );
  }, [coursesData]);

  if (loading) return <Loader />;
  if (error) return <ErrorAnimationComponent message={error} />;

  return (
    <>
      <div className="tw-flex max-md:tw-flex-col justify-content-between tw-items-center">
        <PageTitle title="Courses Management" />
        <LightButton
          hoverColor={"hover:!tw-text-blueLight"}
          text={"Add New Course"}
          icon={<span className="material-icons">add</span>}
          to={"/create-course"}
        />
      </div>
      <div className="bg-white flex flex-col mt-4">
        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <DataTable
            columns={columns}
            data={coursesData}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={loading}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />
          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={paginator?.last_page}
              currentPage={paginator?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ListCoursesManagement;
